import SecureInput from '../../../../../ui/SecureInput/SecureInput';
import styles from './VoiceConfigTab.module.css';

export interface VoiceConfigValues {
  voiceEnabled: boolean;
  drivingModeEnabled: boolean;
  voiceOverride: boolean;
  voiceTtsProvider: string;
  voiceTtsModel: string;
  voiceTtsVoice: string;
  voiceApiKeyOpenai: string;
  voiceGoogleApiKey: string;
  voiceGoogleVoice: string;
  voiceGoogleEnVoice: string;
  voiceElevenLabsApiKey: string;
  voiceElevenLabsVoiceId: string;
}

interface Props {
  config: VoiceConfigValues;
  onChange: (partial: Partial<VoiceConfigValues>) => void;
}

const TTS_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'google', label: 'Google Cloud TTS (Best for Hebrew)' },
  { value: 'elevenlabs', label: 'ElevenLabs (Multilingual)' },
];

const OPENAI_MODELS = [
  { value: 'tts-1', label: 'TTS-1 (Fast)' },
  { value: 'tts-1-hd', label: 'TTS-1 HD (Higher quality)' },
];

const OPENAI_VOICES = [
  { value: 'nova', label: 'Nova (Female, warm)' },
  { value: 'alloy', label: 'Alloy (Neutral)' },
  { value: 'echo', label: 'Echo (Male)' },
  { value: 'fable', label: 'Fable (Expressive)' },
  { value: 'onyx', label: 'Onyx (Male, deep)' },
  { value: 'shimmer', label: 'Shimmer (Female, clear)' },
];

const GOOGLE_HE_VOICES = [
  { value: 'he-IL-Wavenet-A', label: 'Hebrew Wavenet A (Female)' },
  { value: 'he-IL-Wavenet-B', label: 'Hebrew Wavenet B (Male)' },
  { value: 'he-IL-Wavenet-C', label: 'Hebrew Wavenet C (Female)' },
  { value: 'he-IL-Wavenet-D', label: 'Hebrew Wavenet D (Male) - Recommended' },
  { value: 'he-IL-Standard-A', label: 'Hebrew Standard A (Female)' },
  { value: 'he-IL-Standard-B', label: 'Hebrew Standard B (Male)' },
  { value: 'he-IL-Standard-C', label: 'Hebrew Standard C (Female)' },
  { value: 'he-IL-Standard-D', label: 'Hebrew Standard D (Male)' },
];

const GOOGLE_EN_VOICES = [
  { value: 'en-US-Wavenet-A', label: 'English Wavenet A (Male)' },
  { value: 'en-US-Wavenet-B', label: 'English Wavenet B (Male)' },
  { value: 'en-US-Wavenet-C', label: 'English Wavenet C (Female)' },
  { value: 'en-US-Wavenet-D', label: 'English Wavenet D (Male) - Recommended' },
  { value: 'en-US-Wavenet-E', label: 'English Wavenet E (Female)' },
  { value: 'en-US-Wavenet-F', label: 'English Wavenet F (Female)' },
  { value: 'en-US-Standard-A', label: 'English Standard A (Male)' },
  { value: 'en-US-Standard-B', label: 'English Standard B (Male)' },
  { value: 'en-US-Standard-C', label: 'English Standard C (Female)' },
  { value: 'en-US-Standard-D', label: 'English Standard D (Male)' },
];

const isKeySet = (value: string) => value && value.startsWith('••');

export default function VoiceConfigTab({ config, onChange }: Props) {
  const provider = config.voiceTtsProvider || 'openai';
  const drivingModeAvailable = config.voiceEnabled;

  return (
    <>
      {/* Voice / TTS Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Voice (Text-to-Speech)</h3>

        <div className={styles.field}>
          <label>Enable Voice</label>
          <div className={styles.toggleRow}>
            <span className={styles.toggleLabel}>Off</span>
            <button
              className={`${styles.switch} ${config.voiceEnabled ? styles.switchOn : ''}`}
              onClick={() => onChange({
                voiceEnabled: !config.voiceEnabled,
                ...(!config.voiceEnabled ? {} : { drivingModeEnabled: false }),
              })}
              type="button" role="switch" aria-checked={config.voiceEnabled}
            >
              <span className={styles.switchThumb} />
            </button>
            <span className={styles.toggleLabel}>On</span>
            <span className={styles.toggleHint}>
              {config.voiceEnabled
                ? 'Users can listen to AI responses and use voice input'
                : 'Voice features are hidden from the chat'}
            </span>
          </div>
        </div>

        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
          Default voice provider and settings are configured in Settings &gt; Chat Voice.
        </p>
      </div>

      {/* Override Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Voice Override</h3>
        <div className={styles.field}>
          <label>Override default voice for this widget</label>
          <div className={styles.toggleRow}>
            <span className={styles.toggleLabel}>Off</span>
            <button
              className={`${styles.switch} ${config.voiceOverride ? styles.switchOn : ''}`}
              onClick={() => onChange({ voiceOverride: !config.voiceOverride })}
              type="button" role="switch" aria-checked={config.voiceOverride}
            >
              <span className={styles.switchThumb} />
            </button>
            <span className={styles.toggleLabel}>On</span>
            <span className={styles.toggleHint}>
              {config.voiceOverride
                ? 'This widget uses its own voice settings instead of the global defaults'
                : 'Using global voice defaults from Settings > Chat Voice'}
            </span>
          </div>
        </div>

        {config.voiceOverride && (
          <>
            <div className={styles.field}>
              <label>TTS Provider</label>
              <select
                value={provider}
                onChange={e => onChange({ voiceTtsProvider: e.target.value })}
              >
                {TTS_PROVIDERS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <p className={styles.fieldHint}>
                {provider === 'google' && 'Google Cloud TTS has native Israeli Hebrew voices (Wavenet) with natural number pronunciation.'}
                {provider === 'elevenlabs' && 'ElevenLabs multilingual v2 supports Hebrew with high quality cloned and preset voices.'}
                {provider === 'openai' && 'OpenAI TTS supports Hebrew but may not pronounce numbers naturally.'}
              </p>
            </div>

            {/* OpenAI settings */}
            {provider === 'openai' && (
              <>
                <div className={styles.field}>
                  <SecureInput
                    fieldType="password"
                    label="OpenAI API Key"
                    skipPasswordValidation
                    value={isKeySet(config.voiceApiKeyOpenai) ? '' : config.voiceApiKeyOpenai}
                    placeholder={isKeySet(config.voiceApiKeyOpenai) ? 'Key is configured' : 'sk-... (leave empty to use global key)'}
                    onChange={value => onChange({ voiceApiKeyOpenai: value })}
                    description="Optional - falls back to the global Chat Voice or Chat LLM key."
                    autoComplete="new-password"
                  />
                  {config.voiceApiKeyOpenai && (
                    <button className={styles.clearBtn} type="button" onClick={() => onChange({ voiceApiKeyOpenai: '' })}>
                      Clear (use global key)
                    </button>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Model</label>
                  <select value={config.voiceTtsModel || 'tts-1'} onChange={e => onChange({ voiceTtsModel: e.target.value })}>
                    {OPENAI_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label>Voice</label>
                  <select value={config.voiceTtsVoice || 'nova'} onChange={e => onChange({ voiceTtsVoice: e.target.value })}>
                    {OPENAI_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
              </>
            )}

            {/* Google Cloud TTS settings */}
            {provider === 'google' && (
              <>
                <div className={styles.field}>
                  <SecureInput
                    fieldType="password"
                    label="Google Cloud API Key"
                    skipPasswordValidation
                    value={isKeySet(config.voiceGoogleApiKey) ? '' : config.voiceGoogleApiKey}
                    placeholder={isKeySet(config.voiceGoogleApiKey) ? 'Key is configured' : 'AIza... (leave empty to use global key)'}
                    onChange={value => onChange({ voiceGoogleApiKey: value })}
                    description="Optional - falls back to the global Chat Voice key."
                    autoComplete="new-password"
                  />
                  {config.voiceGoogleApiKey && (
                    <button className={styles.clearBtn} type="button" onClick={() => onChange({ voiceGoogleApiKey: '' })}>
                      Clear (use global key)
                    </button>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Hebrew Voice (he)</label>
                  <select value={config.voiceGoogleVoice || 'he-IL-Wavenet-D'} onChange={e => onChange({ voiceGoogleVoice: e.target.value })}>
                    {GOOGLE_HE_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                  <p className={styles.fieldHint}>
                    Used when the widget language is Hebrew. Wavenet D (Male) is recommended.
                  </p>
                </div>
                <div className={styles.field}>
                  <label>English Voice (en)</label>
                  <select value={config.voiceGoogleEnVoice || 'en-US-Wavenet-D'} onChange={e => onChange({ voiceGoogleEnVoice: e.target.value })}>
                    {GOOGLE_EN_VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                  <p className={styles.fieldHint}>
                    Used when the widget language is English. Wavenet D (Male) is recommended.
                  </p>
                </div>
              </>
            )}

            {/* ElevenLabs settings */}
            {provider === 'elevenlabs' && (
              <>
                <div className={styles.field}>
                  <SecureInput
                    fieldType="password"
                    label="ElevenLabs API Key"
                    skipPasswordValidation
                    value={isKeySet(config.voiceElevenLabsApiKey) ? '' : config.voiceElevenLabsApiKey}
                    placeholder={isKeySet(config.voiceElevenLabsApiKey) ? 'Key is configured' : 'sk_... (leave empty to use global key)'}
                    onChange={value => onChange({ voiceElevenLabsApiKey: value })}
                    description="Optional - falls back to the global Chat Voice key."
                    autoComplete="new-password"
                  />
                  {config.voiceElevenLabsApiKey && (
                    <button className={styles.clearBtn} type="button" onClick={() => onChange({ voiceElevenLabsApiKey: '' })}>
                      Clear (use global key)
                    </button>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Voice ID</label>
                  <input
                    type="text"
                    value={config.voiceElevenLabsVoiceId || ''}
                    placeholder="e.g., f4ELwVZI11mZ3n6dxgef"
                    onChange={e => onChange({ voiceElevenLabsVoiceId: e.target.value })}
                  />
                  <p className={styles.fieldHint}>
                    Voice ID from your ElevenLabs voice library. Uses the multilingual v2 model.
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Driving Mode Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Driving Mode</h3>

        <div className={styles.field}>
          <label>Enable Driving Mode</label>
          <div className={styles.toggleRow}>
            <span className={styles.toggleLabel}>Off</span>
            <button
              className={`${styles.switch} ${config.drivingModeEnabled ? styles.switchOn : ''}`}
              onClick={() => {
                if (!drivingModeAvailable) return;
                onChange({ drivingModeEnabled: !config.drivingModeEnabled });
              }}
              disabled={!drivingModeAvailable}
              type="button" role="switch" aria-checked={config.drivingModeEnabled}
            >
              <span className={styles.switchThumb} />
            </button>
            <span className={styles.toggleLabel}>On</span>
            <span className={styles.toggleHint}>
              {config.drivingModeEnabled
                ? 'Users can enter driving mode from the portal only'
                : !drivingModeAvailable
                  ? 'Enable Voice above to allow driving mode'
                : 'Driving mode is hidden everywhere'}
            </span>
          </div>
          <p className={styles.fieldHint}>
            Full-screen voice-first interface for hands-free use in the portal. Requires Voice to be enabled.
          </p>
        </div>
      </div>
    </>
  );
}
